import mixpanel from 'mixpanel-browser';

import { MIXPANEL_EVENTS } from 'utilities/constants/mixpanel';

let sessionStartTime: number | null = null;
let inactivityTimer: ReturnType<typeof setTimeout> | null = null;

let searchCount = 0;
let datasetEngagementCount = 0;
let exportOrDownloadCount = 0;
let comparisonCount = 0;

const INACTIVITY_TIMEOUT_MS = 10 * 60 * 1000;
const ENGAGEMENT_DURATION_THRESHOLD = 600;

const ACTIVITY_EVENTS = ['mousemove', 'mousedown', 'keydown', 'scroll', 'touchstart'] as const;

export const incrementSearch = () => {
  searchCount++;
};

export const incrementExportOrDownload = () => {
  exportOrDownloadCount++;
};

export const incrementDatasetEngagement = () => {
  datasetEngagementCount++;
};

export const incrementComparison = () => {
  comparisonCount++;
};

export const setupAnalyticsIfAllowed = () => {
  initMixpanel();
  if (!window.Cookiebot.consent.statistics) return;

  mixpanel.opt_in_tracking();
  registerSuperProperties();
  trackSessionStart();
  setupSessionEndTracking();
};

export const disableAnalytics = () => {
  mixpanel.opt_out_tracking();
};

export const initMixpanel = () =>
  mixpanel.init('6c631aa394b4310fa9e23c8955e732dd', {
    debug: !import.meta.env.PROD,
    opt_out_tracking_by_default: true,
    api_host: 'https://api-eu.mixpanel.com',
    persistence: 'localStorage',
  });

const registerSuperProperties = () => {
  mixpanel.register({
    'Session Start Page': window.location.pathname,
    'Screen Resolution': `${screen.width}x${screen.height}`,
  });
};

const trackSessionStart = () => {
  sessionStartTime = Date.now();
  searchCount = 0;
  datasetEngagementCount = 0;
  exportOrDownloadCount = 0;
  comparisonCount = 0;
  mixpanel.track(MIXPANEL_EVENTS.SESSION_STARTED, {
    entry_page: window.location.pathname,
    referrer: document.referrer || 'direct',
  });
};

const clearInactivityTimer = () => {
  if (inactivityTimer !== null) {
    clearTimeout(inactivityTimer);
    inactivityTimer = null;
  }
};

const trackSessionEnd = (useBeacon = false) => {
  if (sessionStartTime === null) return;

  const duration = Math.round((Date.now() - sessionStartTime) / 1000);

  const isEngaged =
    duration >= ENGAGEMENT_DURATION_THRESHOLD ||
    searchCount >= 2 ||
    datasetEngagementCount > 0 ||
    exportOrDownloadCount > 0 ||
    comparisonCount > 0;

  mixpanel.track(
    MIXPANEL_EVENTS.SESSION_ENDED,
    {
      session_duration_seconds: duration,
      exit_page: window.location.pathname,
      is_engaged: isEngaged,
      search_count: searchCount,
      dataset_engagement_count: datasetEngagementCount,
      export_or_download_count: exportOrDownloadCount,
      comparison_count: comparisonCount,
    },
    useBeacon ? { transport: 'sendBeacon' } : undefined
  );
  sessionStartTime = null;
  searchCount = 0;
  datasetEngagementCount = 0;
  exportOrDownloadCount = 0;
  comparisonCount = 0;
  clearInactivityTimer();
};

const resetInactivityTimer = () => {
  clearInactivityTimer();
  if (sessionStartTime !== null && document.visibilityState === 'visible') {
    inactivityTimer = setTimeout(() => trackSessionEnd(), INACTIVITY_TIMEOUT_MS);
  }
};

const setupSessionEndTracking = () => {
  window.addEventListener('pagehide', () => trackSessionEnd(true));

  ACTIVITY_EVENTS.forEach((event) => document.addEventListener(event, resetInactivityTimer, { passive: true }));
  resetInactivityTimer();
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'hidden') {
      clearInactivityTimer();
    } else {
      if (sessionStartTime === null) {
        trackSessionStart();
      }
      resetInactivityTimer();
    }
  });
};
